/**
 * ProfilesPage — Devices ▸ Profiles
 *
 * Mirrors the UX mock:
 *   - Toolbar: round search icon + "Profiles" title + "N TOTAL PROFILES" sub.
 *   - Right-side actions: more (...), add (+ with "Add new profile" tooltip),
 *     view-toggle.
 *   - Table columns:
 *       checkbox | PROFILE NAME ↑ | SITE | ORGANIZATION DEFAULT |
 *       DESCRIPTION | row-actions (edit / copy / delete).
 *   - "Add new profile" / "Edit profile": a centered modal with a left rail
 *     of section tabs (General settings, Video settings, Reporting settings,
 *     Location service) matching the latest UX mock.
 *
 * Data layer:
 *   The Profiles backend is not yet defined, so this page keeps its rows in
 *   local component state, seeded with the example "High Theft Store" row
 *   from the mock. Wiring to a future `/api/orgs/{orgId}/device-profiles`
 *   endpoint should only require swapping the `useState(SEED)` block for a
 *   `useEffect` fetch and the `setRows(...)` mutators for service calls.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import deviceService from '../services/deviceService';
import { useTenant } from '../context/TenantContext';

/* ---------- Mapping helpers between server DTO ↔ table row / form ---------- */

function dtoToRow(dto) {
  // Site assignments live in device_profile_site_assignments (backend table) and
  // arrive on the DTO as siteIds. For SITE-scoped profiles that's the explicit
  // list the admin picked; for ORG-scoped profiles the backend resolves it to
  // every site of the org. The "Sites" column on the listing page shows this
  // resolved count — no longer derived from the JSON settings blob.
  const siteIdsResolved = Array.isArray(dto?.siteIds)
    ? dto.siteIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
    : [];
  const sitesCount = siteIdsResolved.length;
  // Counts for the STATUS pill (applied / total). The DTO field names are
  // best-effort — when the backend doesn't return them yet we fall back to
  // zeros so the pill still renders ("0/0").
  const applied =
    Number(dto?.appliedDeviceCount ?? dto?.appliedCount ?? dto?.appliedDevices ?? 0) || 0;
  const totalDevices =
    Number(dto?.totalDeviceCount   ?? dto?.totalDevices ?? dto?.assignableDeviceCount ?? applied) || 0;
  return {
    id: dto.profileId,
    name: dto.profileName,
    description: dto.description || '',
    isDefault: !!dto.isDefault,
    sites: sitesCount,
    appliedDevices: applied,
    totalDevices,
    // Scope + resolved site list from the backend's device_profile_site_assignments
    // (ORG-scoped profiles report every site of the org; SITE-scoped report the
    // explicitly chosen sites). Retained so Edit and Duplicate can round-trip
    // the assignment without a second GET.
    scope: dto.scope || (siteIdsResolved.length > 0 ? 'SITE' : 'ORG'),
    siteIds: siteIdsResolved,
    // Keep raw settings around so opening for edit can rehydrate the form
    // without a second GET roundtrip.
    settings: dto.settings || null,
  };
}

// countAssociatedSites — removed (deprecated; site assignments now live in
// device_profile_site_assignments table and arrive on the DTO as siteIds).

// Build the JSONB `settings` payload from the modal form state.
// Note: associatedSites/siteIds are intentionally NOT written here — site
// assignments live in the device_profile_site_assignments table and are sent
// via the top-level `scope` + `siteIds` request fields, not via settings JSON.
function formToSettings(form) {
  return {
    general: {
      maxPrivacyDurationMinutes: Number(form.maxPrivacyDurationMinutes) || 60,
      privacyAlertIntervalMinutes: Number(form.privacyAlertIntervalMinutes) || 10,
      longPressDurationSeconds: Number(form.longPressDurationSeconds) || 2,
      startRecordConfirm: !!form.startRecordConfirm,
      stopRecordConfirm: !!form.stopRecordConfirm,
      enablePrivacyMode: !!form.enablePrivacyMode,
      enablePrivacyDurationLimit: !!form.enablePrivacyDurationLimit,
      enablePeriodicPrivacyAlert: !!form.enablePeriodicPrivacyAlert,
      covertMode: !!form.covertMode,
    },
    video: {
      videoRecording: { ...form.videoRecording },
      videoStreaming: { ...form.videoStreaming },
      // videoPreview is not in scope — not sent to the API
      preRecordBuffer1Seconds: Number(form.preRecordBuffer1Seconds) || 30,
      preRecordBuffer2Seconds: Number(form.preRecordBuffer2Seconds) || 30,
      audioSettings: form.audioSettings,
      msiLogoWatermark: !!form.msiLogoWatermark,
      timestampWatermark: !!form.timestampWatermark,
      dewarping360: !!form.dewarping360,
      deviceIdWatermark: !!form.deviceIdWatermark,
      hdr: !!form.hdr,
    },
    reporting: {
      deviceHeartbeatIntervalSeconds: Number(form.deviceHeartbeatIntervalSeconds) || 60,
      deviceRadioReportIntervalSeconds: Number(form.deviceRadioReportIntervalSeconds) || 60,
      websocketKeepAliveIntervalSeconds: Number(form.websocketKeepAliveIntervalSeconds) || 60,
    },
    location: {
      locBurstIntervalMs: Number(form.locBurstIntervalMs) || 1000,
      locFtmPerBurst: Number(form.locFtmPerBurst) || 16,
      locBandwidthMHz: Number(form.locBandwidthMHz) || 40,
      locScanFrequencySeconds: Number(form.locScanFrequencySeconds) || 2,
      locReportingIntervalSeconds: Number(form.locReportingIntervalSeconds) || 60,
      emergencyLocReportingIntervalSeconds: Number(form.emergencyLocReportingIntervalSeconds) || 5,
      indoorLocationService: !!form.indoorLocationService,
      surveyMode: !!form.surveyMode,
      rttIndoorLocation: !!form.rttIndoorLocation,
      imuSensorReporting: !!form.imuSensorReporting,
      useSkyhook: !!form.useSkyhook,
    },
    workerSafety: {
      fallAlert: {
        enabled: !!form.fallAlertEnabled,
        sensitivity: form.fallAlertSensitivity,
        stationaryTimeBeforeAlarmSeconds: Number(form.stationaryTimeBeforeAlarmSeconds) || 100,
        timeBeforeAlarmEscalationSeconds: Number(form.timeBeforeAlarmEscalationSeconds) || 10,
      },
      loneWorker: {
        enabled: !!form.loneWorkerEnabled,
        promptTimerSeconds: Number(form.loneWorkerPromptTimer) || 10,
        responseTimeoutSeconds: Number(form.loneWorkerResponseTimeout) || 20,
      },
    },
  };
}

// Inverse: when editing an existing row, hydrate the form from settings JSON.
// Note: associatedSites is NOT read here — site assignments come from the
// dedicated mapping table (device_profile_site_assignments) and are applied
// by openEdit() from row.siteIds.
function settingsToFormPatch(settings) {
  if (!settings || typeof settings !== 'object') return {};
  const g = settings.general || {};
  const v = settings.video || {};
  const r = settings.reporting || {};
  const l = settings.location || {};
  const w = settings.workerSafety || {};
  const fa = w.fallAlert || {};
  const lw = w.loneWorker || {};
  return {
    maxPrivacyDurationMinutes: String(g.maxPrivacyDurationMinutes ?? '60'),
    privacyAlertIntervalMinutes: String(g.privacyAlertIntervalMinutes ?? '10'),
    longPressDurationSeconds: String(g.longPressDurationSeconds ?? '2'),
    startRecordConfirm: !!g.startRecordConfirm,
    stopRecordConfirm: !!g.stopRecordConfirm,
    enablePrivacyMode: !!g.enablePrivacyMode,
    enablePrivacyDurationLimit: !!g.enablePrivacyDurationLimit,
    enablePeriodicPrivacyAlert: !!g.enablePeriodicPrivacyAlert,
    covertMode: !!g.covertMode,
    videoRecording: { ...VIDEO_DEFAULTS,         ...(v.videoRecording || {}) },
    videoStreaming: { ...VIDEO_DEFAULTS,         ...(v.videoStreaming || {}) },
    videoPreview:   { ...PREVIEW_VIDEO_DEFAULTS, ...(v.videoPreview   || {}) },
    preRecordBuffer1Seconds: String(v.preRecordBuffer1Seconds ?? '30'),
    preRecordBuffer2Seconds: String(v.preRecordBuffer2Seconds ?? '30'),
    audioSettings: v.audioSettings ?? 'On',
    msiLogoWatermark: v.msiLogoWatermark !== false,
    timestampWatermark: v.timestampWatermark !== false,
    dewarping360: v.dewarping360 !== false,
    deviceIdWatermark: v.deviceIdWatermark !== false,
    hdr: v.hdr !== false,
    deviceHeartbeatIntervalSeconds: String(r.deviceHeartbeatIntervalSeconds ?? '60'),
    deviceRadioReportIntervalSeconds: String(r.deviceRadioReportIntervalSeconds ?? '60'),
    websocketKeepAliveIntervalSeconds: String(r.websocketKeepAliveIntervalSeconds ?? '60'),
    locBurstIntervalMs: String(l.locBurstIntervalMs ?? '1000'),
    locFtmPerBurst: String(l.locFtmPerBurst ?? '16'),
    locBandwidthMHz: String(l.locBandwidthMHz ?? '40'),
    locScanFrequencySeconds: String(l.locScanFrequencySeconds ?? '2'),
    locReportingIntervalSeconds: String(l.locReportingIntervalSeconds ?? '60'),
    emergencyLocReportingIntervalSeconds: String(l.emergencyLocReportingIntervalSeconds ?? '5'),
    indoorLocationService: l.indoorLocationService !== false,
    surveyMode: l.surveyMode !== false,
    rttIndoorLocation: l.rttIndoorLocation !== false,
    imuSensorReporting: !!l.imuSensorReporting,
    useSkyhook: l.useSkyhook !== false,
    fallAlertEnabled: fa.enabled !== false,
    fallAlertSensitivity: fa.sensitivity ?? 'Medium',
    stationaryTimeBeforeAlarmSeconds: String(fa.stationaryTimeBeforeAlarmSeconds ?? '100'),
    timeBeforeAlarmEscalationSeconds: String(fa.timeBeforeAlarmEscalationSeconds ?? '10'),
    loneWorkerEnabled: lw.enabled !== false,
    loneWorkerPromptTimer: String(lw.promptTimerSeconds ?? '10'),
    loneWorkerResponseTimeout: String(lw.responseTimeoutSeconds ?? '20'),
  };
}

const SECTIONS = [
  { key: 'general',   label: 'General settings',        icon: 'fa-cog' },
  { key: 'video',     label: 'Video settings',          icon: 'fa-play-circle' },
  { key: 'reporting', label: 'Reporting settings',      icon: 'fa-clipboard-list' },
  { key: 'location',  label: 'Location service',        icon: 'fa-map-marker-alt' },
  { key: 'worker',    label: 'Worker safety settings',  icon: 'fa-user-shield' },
];

// Privacy mode fields are now free-form numeric inputs (range 1-1440).
// The old option arrays have been removed.
// longPressDurationSeconds is now a free-form numeric input (range 1-60).

// Video settings
const RESOLUTION_OPTIONS         = ['480p', '720p', '1080p'];
const PREVIEW_RESOLUTION_OPTIONS = ['240p'];
const QUALITY_OPTIONS            = ['Low', 'Medium', 'High'];
const FRAMERATE_OPTIONS          = ['15', '30'];
// Pre-recording buffer: display labels mapped to stored values (seconds).
// value is what the backend/settings JSON receives; label is what the UI shows.
const PRE_BUFFER_OPTIONS = [
  { value: '0',   label: '0s' },
  { value: '30',  label: '30s' },
  { value: '60',  label: '1min' },
  { value: '120', label: '2min' },
  { value: '90',  label: '90s' },
];
const AUDIO_OPTIONS      = ['Off', 'On'];

// Reporting settings — interval seconds
const HEARTBEAT_OPTIONS = ['5', '30', '60'];

const WS_KEEPALIVE_OPTIONS = ['15', '30', '60', '120', '300'];

// Location service settings
const BURST_INTERVAL_OPTIONS = ['500', '1000', '2000'];
const FTM_PER_BURST_OPTIONS  = ['8', '16', '32'];
const BANDWIDTH_OPTIONS         = ['20', '40', '80', '160'];
const SCAN_FREQUENCY_OPTIONS    = ['2', '3', '5', '10'];
const LOC_REPORTING_OPTIONS     = ['5', '30', '60', '120', '180'];
const EMERGENCY_LOC_REPORTING_OPTIONS = ['5', '30', '60', '90', '120', '180'];


const VIDEO_DEFAULTS = {
  resolution: '1080p',
  quality: 'High',
  frameRate: '30',
};

const PREVIEW_VIDEO_DEFAULTS = {
  resolution: '240p',
  quality: 'Low',
  // frameRate intentionally omitted — preview frame rate is not configurable
};

function emptyForm() {
  return {
    name: '',
    description: '',
    associatedSites: [], // array of site IDs (numbers)
    isDefault: false,
    hdr: true,
    maxPrivacyDurationMinutes: '60',
    privacyAlertIntervalMinutes: '10',
    longPressDurationSeconds: '2',
    startRecordConfirm: false,
    stopRecordConfirm: false,
    enablePrivacyMode: false,
    enablePrivacyDurationLimit: false,
    enablePeriodicPrivacyAlert: false,
    covertMode: false,
    sites: 0,
    // Video settings
    videoRecording: { ...VIDEO_DEFAULTS },
    videoStreaming: { ...VIDEO_DEFAULTS },
    videoPreview:   { ...PREVIEW_VIDEO_DEFAULTS },
    preRecordBuffer1Seconds: '30',
    preRecordBuffer2Seconds: '30',
    audioSettings: 'On',
    msiLogoWatermark: true,
    timestampWatermark: true,
    dewarping360: true,
    deviceIdWatermark: true,
    // Reporting settings
    deviceHeartbeatIntervalSeconds: '60',
    deviceRadioReportIntervalSeconds: '60',
    websocketKeepAliveIntervalSeconds: '60',
    // Location service settings
    locBurstIntervalMs: '1000',
    locFtmPerBurst: '16',
    locBandwidthMHz: '40',
    locScanFrequencySeconds: '2',
    locReportingIntervalSeconds: '60',
    emergencyLocReportingIntervalSeconds: '5',
    indoorLocationService: true,
    surveyMode: true,
    rttIndoorLocation: true,
    imuSensorReporting: false,
    useSkyhook: true,
    // Worker safety settings
    fallAlertEnabled: true,
    fallAlertSensitivity: 'Medium',
    stationaryTimeBeforeAlarmSeconds: '100',
    timeBeforeAlarmEscalationSeconds: '10',
    loneWorkerEnabled: true,
    loneWorkerPromptTimer: '10',
    loneWorkerResponseTimeout: '20',
  };
}

export default function ProfilesPage() {
  const navigate = useNavigate();
  const { orgId, sites: tenantSites } = useTenant();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [sort, setSort] = useState({ key: 'name', asc: true });
  // Per-site body-camera counts: Map<siteId, number>. Used by the "Associated
  // sites" dropdown in the Add/Edit profile modal to render "(Body cameras: N)"
  // next to each site, matching the May 2026 Figma.
  const [deviceCountsBySite, setDeviceCountsBySite] = useState(() => new Map());

  // Modal state — `editingId === null && modalOpen === true` ⇒ create.
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeSection, setActiveSection] = useState('general');
  const [form, setForm] = useState(emptyForm());
  // Save-confirmation dialog (shown on top of the modal after the user clicks Save).
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Validation errors for free-form numeric fields.
  const [formErrors, setFormErrors] = useState({});

  const total = rows.length;
  const totalAppliedToDevices = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.appliedDevices) || 0), 0),
    [rows],
  );

  // Resolve associated-site IDs → site names (used for hover tooltip on the
  // "N sites" link in the ASSOCIATED TO column).
  const sitesById = useMemo(() => {
    const m = new Map();
    (tenantSites || []).forEach((s) => {
      const id = s.siteId ?? s.id;
      const name = s.siteName ?? s.name ?? `Site ${id}`;
      m.set(id, name);
      m.set(String(id), name);
    });
    return m;
  }, [tenantSites]);
  const siteNamesFor = (row) => {
    // Site names for the listing tooltip/pill come from row.siteIds, which is
    // populated by dtoToRow from the backend's device_profile_site_assignments
    // mapping table (and resolves to all org sites for ORG-scoped profiles).
    const ids = Array.isArray(row?.siteIds) ? row.siteIds : [];
    return ids
      .map((id) => sitesById.get(id) ?? sitesById.get(String(id)) ?? `Site ${id}`)
      .filter(Boolean);
  };

  // Load profiles from the backend whenever the active org changes.
  useEffect(() => {
    if (!orgId) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    deviceService.listDeviceProfiles(orgId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setRows(list.map(dtoToRow));
      })
      .catch((e) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Failed to load profiles', e);
        setError(e?.response?.data?.message || e.message || 'Failed to load profiles');
        setRows([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId]);

  // Tally body-camera counts per site for the modal's site-picker labels.
  useEffect(() => {
    if (!orgId) { setDeviceCountsBySite(new Map()); return; }
    let cancelled = false;
    deviceService.listDevices(orgId, undefined, { limit: 1000, offset: 0 })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.items || data?.content || []);
        const map = new Map();
        list.forEach((d) => {
          const sid = d.siteId ?? d.site?.siteId ?? d.site?.id;
          if (sid == null) return;
          map.set(sid, (map.get(sid) || 0) + 1);
        });
        setDeviceCountsBySite(map);
      })
      .catch(() => { if (!cancelled) setDeviceCountsBySite(new Map()); });
    return () => { cancelled = true; };
  }, [orgId]);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q
      ? rows
      : rows.filter((r) =>
          [r.name, r.description, r.isDefault ? 'enabled' : 'disabled']
            .filter(Boolean).join(' ').toLowerCase().includes(q)
        );
    return [...base].sort((a, b) => {
      const get = (r) => {
        switch (sort.key) {
          case 'name':        return r.name || '';
          case 'sites':       return Number(r.sites || 0);
          case 'status':      return Number(r.appliedDevices || 0);
          case 'isDefault':   return r.isDefault ? 1 : 0;
          case 'description': return r.description || '';
          default:            return '';
        }
      };
      const av = get(a); const bv = get(b);
      if (typeof av === 'number' && typeof bv === 'number') {
        return (sort.asc ? 1 : -1) * (av - bv);
      }
      return (sort.asc ? 1 : -1) * String(av).localeCompare(String(bv));
    });
  }, [rows, search, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));
  const sortIcon = (key) =>
    sort.key === key ? (sort.asc ? 'fa-sort-up' : 'fa-sort-down') : '';

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) =>
    prev.size === filteredSorted.length ? new Set() : new Set(filteredSorted.map((r) => r.id))
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setActiveSection('general');
    setModalOpen(true);
  };
  const openEdit = async (row) => {
    // Open the modal immediately with whatever the listing has (so it feels
    // responsive), then re-hydrate from a fresh GET /profiles/{id} so the
    // "Associated sites" multi-select always reflects the authoritative state
    // of device_profile_site_assignments — even if the listing was loaded
    // before this column was populated or rows have changed since.
    setEditingId(row.id);
    const hydrate = (src) => ({
      ...emptyForm(),
      name: src.name ?? row.name,
      description: src.description ?? row.description,
      sites: Array.isArray(src.siteIds) ? src.siteIds.length : row.sites,
      isDefault: !!(src.isDefault ?? row.isDefault),
      ...settingsToFormPatch(src.settings ?? row.settings),
      // Site assignments come exclusively from the mapping table
      // (device_profile_site_assignments). For SITE-scoped profiles we
      // pre-select those rows; for ORG-scoped (including isDefault) we leave
      // the selector empty so the admin can explicitly opt into SITE-scope.
      associatedSites: src.scope === 'SITE' && Array.isArray(src.siteIds) ? src.siteIds : [],
    });
    setForm(hydrate(row));
    setActiveSection('general');
    setModalOpen(true);

    try {
      const fresh = await deviceService.getDeviceProfile(row.id, orgId);
      const freshRow = dtoToRow(fresh);
      // Refresh the cached row in the listing so the "Sites" column count and
      // any reopen of the same profile stay in sync with the mapping table.
      setRows((rs) => rs.map((r) => (r.id === freshRow.id ? freshRow : r)));
      setForm(hydrate(freshRow));
    } catch (e) {
      // Non-fatal — the modal is already populated from the listing row.
      // eslint-disable-next-line no-console
      console.warn('Failed to refresh profile for edit', e);
    }
  };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setConfirmOpen(false); };

  const handleCopy = async (row) => {
    if (!orgId) return;
    try {
      // Preserve the source profile's scope + site assignments when duplicating.
      const sourceSiteIds = Array.isArray(row.siteIds)
        ? row.siteIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
        : [];
      const sourceScope = row.scope || (sourceSiteIds.length > 0 ? 'SITE' : 'ORG');
      const created = await deviceService.createDeviceProfile({
        orgId,
        profileName: `${row.name} (copy)`,
        description: row.description,
        settings: row.settings || {},
        isDefault: false,
        scope: sourceScope,
        siteIds: sourceScope === 'SITE' ? sourceSiteIds : [],
      }, orgId);
      setRows((rs) => [...rs, dtoToRow(created)]);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.message || e.message || 'Failed to duplicate profile');
    }
  };
  const handleDelete = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete profile "${row.name}"?`)) return;
    try {
      await deviceService.deleteDeviceProfile(row.id, orgId);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setSelected((prev) => { const n = new Set(prev); n.delete(row.id); return n; });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.message || e.message || 'Failed to delete profile');
    }
  };

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  // Setter for nested video-group fields, e.g. setVideoField('videoRecording', 'quality').
  const setVideoField = (group, sub) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [group]: { ...f[group], [sub]: value } }));
  };

  // Setter for free-form numeric fields with min/max validation.
  // Allows typing freely but records an error message when out of range.
  const setNumericRangeField = (key, min, max) => (e) => {
    const raw = e.target.value;
    setForm((f) => ({ ...f, [key]: raw }));
    if (raw === '') {
      setFormErrors((fe) => ({ ...fe, [key]: 'Value is required (' + min + '\u2013' + max + ').' }));
      return;
    }
    const n = Number(raw);
    if (!Number.isInteger(n) || n < min || n > max) {
      setFormErrors((fe) => ({ ...fe, [key]: 'Must be a whole number between ' + min + ' and ' + max + '.' }));
    } else {
      setFormErrors((fe) => { const next = { ...fe }; delete next[key]; return next; });
    }
  };

  // Disable save when there are outstanding client-side validation errors.
  const canSubmit = Object.keys(formErrors).length === 0;

  // Save click in the modal footer — opens the confirmation dialog.
  // The actual persistence happens in handleConfirmSave below.
  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    let skipConfirm = false;
    try { skipConfirm = localStorage.getItem('profiles.newProfileDialog.hide') === '1'; } catch { /* ignore */ }
    if (skipConfirm) {
      handleConfirmSave();
    } else {
      setConfirmOpen(true);
    }
  };

  const handleConfirmSave = async () => {
    if (!orgId) {
      // eslint-disable-next-line no-alert
      alert('Please select an organization first.');
      return;
    }
    // Derive scope + siteIds for the backend's site-assignment table.
    // Precedence rule (per UX): the "Set as organization default" checkbox
    // ALWAYS wins. If checked, the profile is org-wide and any sites the user
    // may have accidentally selected are ignored. Only when the checkbox is
    // unchecked do we honour the "Associated sites" multi-select to mark the
    // profile as SITE-scoped.
    const associatedSites = Array.isArray(form.associatedSites)
      ? form.associatedSites.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
      : [];
    const isDefault = !!form.isDefault;
    const scope = (!isDefault && associatedSites.length > 0) ? 'SITE' : 'ORG';
    const payload = {
      orgId,
      profileName: form.name.trim() || 'Untitled profile',
      description: form.description.trim(),
      settings: formToSettings(form),
      isDefault,
      scope,
      siteIds: scope === 'SITE' ? associatedSites : [],
    };
    setSaving(true);
    try {
      if (editingId == null) {
        const created = await deviceService.createDeviceProfile(payload, orgId);
        setRows((rs) => {
          const next = form.isDefault ? rs.map((r) => ({ ...r, isDefault: false })) : rs;
          return [...next, dtoToRow(created)];
        });
      } else {
        const updated = await deviceService.updateDeviceProfile(editingId, payload, orgId);
        const updatedRow = dtoToRow(updated);
        setRows((rs) => rs.map((r) => {
          if (r.id === editingId) return updatedRow;
          return form.isDefault ? { ...r, isDefault: false } : r;
        }));
      }
      setConfirmOpen(false);
      closeModal();
      // After saving, return to the Profiles listing page. Closing the modal
      // already leaves the user on this page, but we navigate explicitly so the
      // URL is canonical (e.g. if the modal was deep-linked) and any future
      // routing changes don't break the post-save flow.
      navigate('/devices/profiles');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to save profile', e);
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.message || e.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search profiles"
            onClick={() => setShowSearch((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>
          <div className="sp-heading">
            <h1>Profiles</h1>
            <div className="up-summary">
              <span className="up-summary-item"><strong>{total}</strong> TOTAL</span>
              <span className="up-summary-item">
                <span className="up-dot up-dot-active" /> {totalAppliedToDevices} APPLIED TO DEVICES
              </span>
            </div>
            {showSearch && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Search profiles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="btn-primary-pill" onClick={openCreate}>
            <i className="fas fa-plus"></i> Add profile
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      <div className="cameras-table-card">
        <table className="data-table cameras-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={filteredSorted.length > 0 && selected.size === filteredSorted.length}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th>
                <button className="data-table-sort" onClick={() => toggleSort('name')}>
                  PROFILE NAME {sortIcon('name') && <i className={`fas ${sortIcon('name')}`}></i>}
                </button>
              </th>
              <th><button className="data-table-sort" onClick={() => toggleSort('sites')}>ASSOCIATED TO</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('status')}>STATUS</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('isDefault')}>ORGANIZATION DEFAULT</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('description')}>DESCRIPTION</button></th>
              <th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                  <i className="fas fa-spinner fa-spin" /> Loading profiles…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#fca5a5' }}>
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && filteredSorted.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                  No profiles yet. Click <strong>+ Add profile</strong> to create one.
                </td>
              </tr>
            )}
            {!loading && !error && filteredSorted.map((r) => (
              <tr key={r.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggleRow(r.id)}
                    aria-label={`Select ${r.name}`}
                  />
                </td>
                <td>{r.name}</td>
                <td>
                  {(() => {
                    const names = siteNamesFor(r);
                    return (
                      <span className="pp-tip-wrap">
                        <button type="button" className="pp-link" onClick={() => openEdit(r)}>
                          {r.sites === 1 ? '1 Site' : `${r.sites} Sites`}
                        </button>
                        {names.length > 0 && (
                          <span className="pp-tooltip" role="tooltip">
                            <strong>Associated to sites:</strong>
                            <ul>
                              {names.map((n, i) => <li key={`${n}-${i}`}>{n}</li>)}
                            </ul>
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </td>
                <td>
                  {(() => {
                    const applied = Number(r.appliedDevices) || 0;
                    const totalDev = Number(r.totalDevices) || applied;
                    const isWarn  = totalDev > 0 && applied < totalDev;
                    return (
                      <span className="pp-tip-wrap">
                        <span className={`pp-status-pill ${isWarn ? 'pp-status-pill-warn' : 'cam-status cam-status-active'}`}>
                          <i className={`fas ${isWarn ? 'fa-exclamation' : 'fa-circle'}`}></i>
                          {applied}/{totalDev}
                        </span>
                        {isWarn && (
                          <span className="pp-tooltip pp-tooltip-status" role="tooltip">
                            The profile will be applied automatically once all body cameras are docked
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </td>
                <td>{r.isDefault ? 'Enabled' : <span className="up-muted">-</span>}</td>
                <td>{r.description || <span className="up-muted">-</span>}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="row-action" title="Edit" onClick={() => openEdit(r)}>
                    <i className="fas fa-pencil-alt"></i>
                  </button>
                  <button className="row-action" title="Duplicate" onClick={() => handleCopy(r)}>
                    <i className="fas fa-copy"></i>
                  </button>
                  <button className="row-action row-action-danger" title="Delete" onClick={() => handleDelete(r)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="list-footer" hidden aria-hidden="true">
        <span className="list-footer-count">{total} profiles</span>
      </div>

      {modalOpen && (
        <ProfileModal
          editing={editingId != null}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          form={form}
          setField={setField}
          setForm={setForm}
          setVideoField={setVideoField}
          setNumericRangeField={setNumericRangeField}
          formErrors={formErrors}
          sites={tenantSites}
          deviceCountsBySite={deviceCountsBySite}
          onClose={closeModal}
          onSubmit={handleSubmit}
          canSubmit={canSubmit}
        />
      )}

      {modalOpen && confirmOpen && (
        <ConfirmDialog
          title="New profile"
          message="To apply the profile, ensure body cameras are docked. The new configuration will sync automatically to all devices."
          onCancel={() => { if (!saving) setConfirmOpen(false); }}
          onConfirm={handleConfirmSave}
          confirmLabel={saving ? 'Saving…' : 'OK'}
          confirmDisabled={saving}
          showDontShowAgain
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */
/* Modal                                                                     */
/* ------------------------------------------------------------------------ */

function ProfileModal({
  editing, activeSection, setActiveSection,
  form, setField, setForm, setVideoField, setNumericRangeField, formErrors, sites, deviceCountsBySite, onClose, onSubmit, /* canSubmit */
}) {
  return (
    <div
      className="profile-modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="profile-modal" role="dialog" aria-label={editing ? 'Edit profile' : 'Add profile'}>
        <header className="profile-modal-header">
          <h2>{editing ? 'Edit profile' : 'Add profile'}</h2>
          <p>Add a new configuration set to the system. To deploy the profile, ensure the body camera is docked</p>
        </header>

        <form className="profile-modal-body" onSubmit={onSubmit} noValidate>
          <nav className="profile-modal-tabs" aria-label="Profile sections">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`profile-modal-tab ${activeSection === s.key ? 'active' : ''}`}
                onClick={() => setActiveSection(s.key)}
              >
                <i className={`fas ${s.icon}`}></i>
                <span>{s.label}</span>
              </button>
            ))}
          </nav>

          <div className="profile-modal-content">
            {activeSection === 'general'   && <GeneralSettings form={form} setField={setField} setForm={setForm} setNumericRangeField={setNumericRangeField} formErrors={formErrors} sites={sites} deviceCountsBySite={deviceCountsBySite} />}
            {activeSection === 'video'     && <VideoSettings form={form} setField={setField} setVideoField={setVideoField} />}
            {activeSection === 'reporting' && <ReportingSettings form={form} setField={setField} setNumericRangeField={setNumericRangeField} formErrors={formErrors} />}
            {activeSection === 'location'  && <LocationSettings form={form} setField={setField} />}
            {activeSection === 'worker'    && <WorkerSafetySettings form={form} setField={setField} />}
          </div>
        </form>

        <footer className="profile-modal-footer">
          <button type="button" className="profile-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="profile-btn-primary"
            onClick={onSubmit}
          >
            Apply
          </button>
        </footer>
      </div>
    </div>
  );
}

function GeneralSettings({ form, setField, setForm, setNumericRangeField, formErrors, sites, deviceCountsBySite }) {
  // Toggling "Set as organization default" should clear any previously chosen
  // sites — an org-default profile applies to every site of the org, so a
  // sites selection is meaningless and would only confuse the admin.
  const onIsDefaultChange = (e) => {
    const checked = !!e.target.checked;
    setForm((f) => ({
      ...f,
      isDefault: checked,
      // Clear the multi-select only when turning the checkbox ON; leave the
      // (already empty) selection alone when turning it off.
      ...(checked ? { associatedSites: [] } : {}),
    }));
  };
  const toggleSite = (siteId) => {
    const current = Array.isArray(form.associatedSites) ? form.associatedSites : [];
    const next = current.includes(siteId)
      ? current.filter((id) => id !== siteId)
      : [...current, siteId];
    setField('associatedSites')({ target: { value: next } });
  };
  const clearSites = () => setField('associatedSites')({ target: { value: [] } });
  const selectAllSites = (allIds) => setField('associatedSites')({ target: { value: allIds } });
  return (
    <>
      {/* Top banner: "Set as organization default" sits above the content
          (May 2026 Figma) — replaces the inline toggle row that used to live
          inside the form. */}
      <label className="pp-banner">
        <input
          type="checkbox"
          checked={!!form.isDefault}
          onChange={onIsDefaultChange}
        />
        <span className="pp-banner-box" aria-hidden="true">
          {!!form.isDefault && <i className="fas fa-check" />}
        </span>
        <span className="pp-banner-text">Set as organization default</span>
        <i
          className="fas fa-question-circle pp-banner-help"
          title="Use this profile as the default for the organization."
        />
      </label>

      {/* ── INFORMATION ─────────────────────────────────────────────── */}
      <h3 className="profile-section-title pp-section-title">INFORMATION</h3>
      <div className="pp-card">
        <div className="profile-grid-2">
          <div className="profile-field">
            <label className="profile-label">Template name</label>
            <input
              type="text"
              className="profile-input"
              value={form.name}
              onChange={setField('name')}
              autoFocus
              required
            />
          </div>
          <div className="profile-field profile-row-span-2">
            <label className="profile-label">Description</label>
            <textarea
              className="profile-input profile-textarea"
              value={form.description}
              onChange={setField('description')}
              rows={4}
            />
          </div>
          <div className="profile-field">
            <label className="profile-label">Associated sites</label>
            <SitesMultiSelect
              sites={sites}
              selected={form.associatedSites}
              onToggle={toggleSite}
              onClear={clearSites}
              onSelectAll={selectAllSites}
              deviceCountsBySite={deviceCountsBySite}
            />
          </div>
        </div>
      </div>

      {/* ── PRIVACY MODE CONFIGURATION ─────────────────────────────── */}
      <h3 className="profile-section-title pp-section-title">PRIVACY MODE CONFIGURATION</h3>
      <div className="pp-card">
        <div className="profile-grid-3">
          <NumericRangeField
            label="Max duration allowed for privacy mode (min)"
            value={form.maxPrivacyDurationMinutes}
            min={1}
            max={1440}
            error={formErrors.maxPrivacyDurationMinutes}
            onChange={setNumericRangeField('maxPrivacyDurationMinutes', 1, 1440)}
          />
          <NumericRangeField
            label="Alert when in privacy mode periodically (min)"
            value={form.privacyAlertIntervalMinutes}
            min={1}
            max={1440}
            error={formErrors.privacyAlertIntervalMinutes}
            onChange={setNumericRangeField('privacyAlertIntervalMinutes', 1, 1440)}
          />
          <NumericRangeField
            label="Long press duration (s)"
            value={form.longPressDurationSeconds}
            min={1}
            max={60}
            error={formErrors.longPressDurationSeconds}
            onChange={setNumericRangeField('longPressDurationSeconds', 1, 60)}
          />
        </div>
      </div>

      {/* ── DEVICE INTERACTION & RECORDING CONTROLS ───────────────── */}
      <h3 className="profile-section-title pp-section-title">DEVICE INTERACTION &amp; RECORDING CONTROLS</h3>
      <div className="pp-card">
        <div className="profile-grid-2">
          <CheckboxField
            label="Covert mode"
            checked={form.covertMode}
            onChange={setField('covertMode')}
          />
          <CheckboxField
            label="Stop recording confirmation"
            checked={form.stopRecordConfirm}
            onChange={setField('stopRecordConfirm')}
          />
          <CheckboxField
            label="Enable privacy mode"
            checked={form.enablePrivacyMode}
            onChange={setField('enablePrivacyMode')}
          />
          <CheckboxField
            label="Enable privacy mode duration limit"
            checked={form.enablePrivacyDurationLimit}
            onChange={setField('enablePrivacyDurationLimit')}
          />
          <CheckboxField
            label={'Enable periodic "In privacy mode" alert'}
            checked={form.enablePeriodicPrivacyAlert}
            onChange={setField('enablePeriodicPrivacyAlert')}
          />
          <CheckboxField
            label="Start recording confirmation"
            checked={form.startRecordConfirm}
            onChange={setField('startRecordConfirm')}
          />
        </div>
      </div>
    </>
  );
}

function SelectField({ label, value, options, onChange, disabled }) {
  return (
    <div className="profile-field">
      <label className="profile-label">{label}</label>
      <div className="profile-select-wrap">
        <select className="profile-input profile-select" value={value} onChange={onChange} disabled={disabled}>
          {options.map((o) => {
            const val = typeof o === 'object' ? o.value : o;
            const lbl = typeof o === 'object' ? o.label : o;
            return <option key={val} value={val}>{lbl}</option>;
          })}
        </select>
        <i className="fas fa-chevron-down profile-select-caret" aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * NumericRangeField � numeric text input with min/max validation and inline error display.
 * Used for free-form integer fields where a dropdown no longer fits the requirement.
 */
function NumericRangeField({ label, value, min, max, error, onChange }) {
  const inputId = label.replace(/\s+/g, '-') + '-input';
  const errorId = label.replace(/\s+/g, '-') + '-error';
  return (
    <div className="profile-field">
      <label className="profile-label" htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        className={`profile-input${error ? ' profile-input-error' : ''}`}
        type="number"
        min={min}
        max={max}
        step="1"
        value={value ?? ''}
        onChange={onChange}
        onKeyDown={(e) => {
          if (['.', ',', '+', '-', 'e', 'E'].includes(e.key)) e.preventDefault();
        }}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
      />
      {error && (
        <span id={errorId} className="profile-field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * TextField — plain text input with an optional unit suffix (e.g. "s" for seconds).
 * Used for free-form numeric interval fields where the discrete dropdown options
 * weren't a good fit (Location service section, May 2026 Figma).
 */
function TextField({ label, value, onChange, unit, placeholder, type = 'text' }) {
  return (
    <div className="profile-field">
      <label className="profile-label">{label}</label>
      <div className="profile-text-wrap" style={{ position: 'relative' }}>
        <input
          className="profile-input"
          type={type}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={onChange}
          style={unit ? { paddingRight: 28 } : undefined}
        />
        {unit ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--profile-muted, #8b95a7)',
              fontSize: 13,
              pointerEvents: 'none',
            }}
          >
            {unit}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * SitesMultiSelect — chip-based multi-select dropdown for associated sites.
 * Open-state menu mirrors the UX mock:
 *   - "SELECT SITES FROM THE LIST" caption
 *   - "Select all sites" master checkbox row
 *   - Each row: checkbox + bold site name + muted "(Body cameras: N)" suffix
 *
 * Props:
 *   sites    — array of {siteId|id, siteName|name, bodyCameras?} from useTenant()
 *   selected — array of site IDs currently selected
 *   onToggle — (siteId) => void; flips one site in/out of the selection
 *   onClear  — () => void; clears all selections
 *   onSelectAll — (allIds) => void; selects every site in the list
 */
function SitesMultiSelect({ sites = [], selected = [], onToggle, onClear, onSelectAll, deviceCountsBySite }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const idOf   = (s) => s.siteId ?? s.id;
  const nameOf = (s) => s.siteName ?? s.name ?? `Site ${idOf(s)}`;
  const camerasOf = (s) => {
    if (Number.isFinite(s?.bodyCameras)) return s.bodyCameras;
    if (Number.isFinite(s?.deviceCount)) return s.deviceCount;
    if (deviceCountsBySite && typeof deviceCountsBySite.get === 'function') {
      const c = deviceCountsBySite.get(idOf(s));
      if (Number.isFinite(c)) return c;
    }
    return 0;
  };
  const sel = Array.isArray(selected) ? selected : [];
  const byId = useMemo(() => {
    const m = new Map();
    sites.forEach((s) => m.set(idOf(s), s));
    return m;
  }, [sites]);

  const allIds = useMemo(() => sites.map((s) => idOf(s)), [sites]);
  const allSelected = sites.length > 0 && sel.length === sites.length;
  const someSelected = sel.length > 0 && !allSelected;
  const masterRef = useRef(null);
  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleAll = () => {
    if (allSelected) onClear();
    else onSelectAll(allIds);
  };

  return (
    <div className={`sites-mselect ${open ? 'open' : ''}`} ref={wrapRef}>
      <div
        role="button"
        tabIndex={0}
        className="sites-mselect-trigger"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
      >
        <div className="sites-mselect-summary">
          {sel.length === 0 && (
            <span className="sites-mselect-placeholder">Select sites…</span>
          )}
          {sel.length > 0 && (
            <span className="sites-mselect-summary-text">
              {sel.length === 1
                ? nameOf(byId.get(sel[0]) || { id: sel[0] })
                : `${sel.length} sites selected`}
            </span>
          )}
        </div>
        <i className="fas fa-chevron-down sites-mselect-caret" aria-hidden="true" />
      </div>

      {open && (
        <div className="sites-mselect-menu" role="listbox">
          <div className="sites-mselect-caption">SELECT SITES FROM THE LIST</div>
          {sites.length === 0 ? (
            <div className="sites-mselect-empty">No sites for this organization</div>
          ) : (
            <>
              <label className="sites-mselect-row sites-mselect-row-all">
                <input
                  ref={masterRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                />
                <span className="sites-mselect-box" aria-hidden="true">
                  {allSelected && <i className="fas fa-check" />}
                  {someSelected && !allSelected && <i className="fas fa-minus" />}
                </span>
                <span className="sites-mselect-label sites-mselect-label-strong">Select all sites</span>
              </label>
              {sites.map((s) => {
                const id = idOf(s);
                const checked = sel.includes(id);
                const cams = camerasOf(s);
                return (
                  <label key={id} className="sites-mselect-row">
                    <input type="checkbox" checked={checked} onChange={() => onToggle(id)} />
                    <span className="sites-mselect-box" aria-hidden="true">
                      {checked && <i className="fas fa-check" />}
                    </span>
                    <span className="sites-mselect-label">
                      <span className="sites-mselect-name">{nameOf(s)}</span>
                      {cams != null && (
                        <span className="sites-mselect-meta"> (Body cameras: {cams})</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label className="profile-checkbox">
      <input type="checkbox" checked={!!checked} onChange={onChange} />
      <span className="profile-checkbox-box" aria-hidden="true">
        {checked && <i className="fas fa-check" />}
      </span>
      <span className="profile-checkbox-label">{label}</span>
    </label>
  );
}

/* ------------------------------------------------------------------------ */
/* Video settings tab                                                        */
/* ------------------------------------------------------------------------ */

function VideoSettings({ form, setField, setVideoField }) {
  return (
    <>
      <h3 className="profile-section-title">VIDEO AND AUDIO SETTINGS</h3>

      <div className="pp-card">
        <VideoTripletRow
          label="Video recording"
          group="videoRecording"
          values={form.videoRecording}
          setVideoField={setVideoField}
        />
        <VideoTripletRow
          label="Video streaming"
          group="videoStreaming"
          values={form.videoStreaming}
          setVideoField={setVideoField}
        />
        <VideoTripletRow
          label="Video preview"
          group="videoPreview"
          values={form.videoPreview}
          setVideoField={setVideoField}
          resolutionOptions={PREVIEW_RESOLUTION_OPTIONS}
          previewMode
        />

        <div className="profile-vrow profile-vrow-divider">
          <div className="profile-vrow-label">Others</div>
          <div className="profile-vrow-fields">
            <SelectField
              label="Pre recording buffer (s)"
              value={form.preRecordBuffer1Seconds}
              options={PRE_BUFFER_OPTIONS}
              onChange={setField('preRecordBuffer1Seconds')}
            />
            <SelectField
              label="Pre recording buffer (s)"
              value={form.preRecordBuffer2Seconds}
              options={PRE_BUFFER_OPTIONS}
              onChange={setField('preRecordBuffer2Seconds')}
            />
            <SelectField
              label="Audio settings"
              value={form.audioSettings}
              options={AUDIO_OPTIONS}
              onChange={setField('audioSettings')}
            />
          </div>
        </div>
      </div>

      <h3 className="profile-section-title pp-section-title">OVERLAYS &amp; IMAGE PROCESSING</h3>
      <div className="pp-card">
        <div className="profile-checkbox-grid profile-checkbox-grid-3">
          <CheckboxField
            label="MSI logo watermark"
            checked={form.msiLogoWatermark}
            onChange={setField('msiLogoWatermark')}
          />
          <CheckboxField
            label="Dewarping"
            checked={form.dewarping360}
            onChange={setField('dewarping360')}
          />
          <label className="profile-checkbox">
            <input
              type="checkbox"
              checked={!!form.hdr}
              onChange={setField('hdr')}
            />
            <span className="profile-checkbox-box" aria-hidden="true">
              {form.hdr && <i className="fas fa-check" />}
            </span>
            <span className="profile-checkbox-label">
              HDR <i className="fas fa-question-circle pp-banner-help" title="High Dynamic Range processing" />
            </span>
          </label>
          <CheckboxField
            label="Timestamp watermark"
            checked={form.timestampWatermark}
            onChange={setField('timestampWatermark')}
          />
          <CheckboxField
            label="Device ID watermark"
            checked={form.deviceIdWatermark}
            onChange={setField('deviceIdWatermark')}
          />
        </div>
      </div>
    </>
  );
}

function VideoTripletRow({ label, group, values, setVideoField, resolutionOptions, previewMode }) {
  return (
    <div className="profile-vrow">
      <div className="profile-vrow-label">{label}</div>
      <div className="profile-vrow-fields">
        <SelectField
          label="Resolution"
          value={values.resolution}
          options={resolutionOptions || RESOLUTION_OPTIONS}
          onChange={setVideoField(group, 'resolution')}
          disabled={previewMode}
        />
        <SelectField
          label="Quality"
          value={values.quality}
          options={QUALITY_OPTIONS}
          onChange={setVideoField(group, 'quality')}
          disabled={previewMode}
        />
        {!previewMode && (
          <SelectField
            label="Frame rate"
            value={values.frameRate}
            options={FRAMERATE_OPTIONS}
            onChange={setVideoField(group, 'frameRate')}
          />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Reporting settings tab                                                    */
/* ------------------------------------------------------------------------ */

function ReportingSettings({ form, setField, setNumericRangeField, formErrors }) {
  return (
    <>
      <h3 className="profile-section-title">REPORTING SETTINGS</h3>

      <div className="pp-card">
        <div className="profile-grid-3 profile-grid-3-plain">
          <SelectField
            label="Device heartbeat interval"
            value={form.deviceHeartbeatIntervalSeconds}
            options={HEARTBEAT_OPTIONS}
            onChange={setField('deviceHeartbeatIntervalSeconds')}
          />
          <NumericRangeField
            label="Device radio report interval (s)"
            value={form.deviceRadioReportIntervalSeconds}
            min={60}
            max={86400}
            error={formErrors.deviceRadioReportIntervalSeconds}
            onChange={setNumericRangeField('deviceRadioReportIntervalSeconds', 60, 86400)}
          />
          <SelectField
            label="Websocket keepAlive interval"
            value={form.websocketKeepAliveIntervalSeconds}
            options={WS_KEEPALIVE_OPTIONS}
            onChange={setField('websocketKeepAliveIntervalSeconds')}
          />
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------ */
/* Location service tab                                                      */
/* ------------------------------------------------------------------------ */

function LocationSettings({ form, setField }) {
  return (
    <>
      <h3 className="profile-section-title">LOCATION PROTOCOL SETTINGS</h3>

      <div className="pp-card">
        <div className="profile-grid-2">
          <SelectField
            label="802.11 MC/az burst interval (ms)"
            value={form.locBurstIntervalMs}
            options={BURST_INTERVAL_OPTIONS}
            onChange={setField('locBurstIntervalMs')}
          />
          <SelectField
            label="802.11 MC/az FTM per burst"
            value={form.locFtmPerBurst}
            options={FTM_PER_BURST_OPTIONS}
            onChange={setField('locFtmPerBurst')}
          />
          <SelectField
            label="802.11 MC/az bandwidth (MHz)"
            value={form.locBandwidthMHz}
            options={BANDWIDTH_OPTIONS}
            onChange={setField('locBandwidthMHz')}
          />
          <SelectField
            label="Survey mode Scan frequency (s)"
            value={form.locScanFrequencySeconds}
            options={SCAN_FREQUENCY_OPTIONS}
            onChange={setField('locScanFrequencySeconds')}
          />
          <SelectField
            label="Location service reporting interval (s)"
            value={form.locReportingIntervalSeconds}
            options={LOC_REPORTING_OPTIONS}
            onChange={setField('locReportingIntervalSeconds')}
          />
          <SelectField
            label='Emergency - Location Service reporting interval (s)'
            value={form.emergencyLocReportingIntervalSeconds}
            options={EMERGENCY_LOC_REPORTING_OPTIONS}
            onChange={setField('emergencyLocReportingIntervalSeconds')}
          />
        </div>
      </div>

      <h3 className="profile-section-title pp-section-title">LOCATION MODULES</h3>
      <div className="pp-card">
        <div className="profile-checkbox-grid profile-checkbox-grid-2">
          <CheckboxField
            label="Indoor location service"
            checked={form.indoorLocationService}
            onChange={setField('indoorLocationService')}
          />
          <CheckboxField
            label="Survey mode"
            checked={form.surveyMode}
            onChange={setField('surveyMode')}
          />
          <CheckboxField
            label="IMU sensor location reporting"
            checked={form.imuSensorReporting}
            onChange={setField('imuSensorReporting')}
          />
          <CheckboxField
            label="802.11MC RTT indoor location"
            checked={form.rttIndoorLocation}
            onChange={setField('rttIndoorLocation')}
          />
          <CheckboxField
            label="Use Skyhook"
            checked={form.useSkyhook}
            onChange={setField('useSkyhook')}
          />
        </div>
      </div>
    </>
  );
}

/* (SectionPlaceholder removed — no longer needed) */

/* ------------------------------------------------------------------------ */
/* Save-confirmation dialog                                                  */
/* Small centered dialog overlaid on top of the profile modal. Used when    */
/* the user clicks Save in the Add new profile / Edit profile modal.        */
/* ------------------------------------------------------------------------ */

function ConfirmDialog({ title, message, onCancel, onConfirm, confirmLabel = 'OK', cancelLabel = 'Cancel', confirmDisabled = false, showDontShowAgain = false }) {
  const [dontShow, setDontShow] = useState(false);
  return (
    <div
      className="confirm-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="confirm-dialog" role="alertdialog" aria-label={title}>
        <header className="confirm-header">
          <h3>{title}</h3>
        </header>
        <div className="confirm-body">
          <p className="confirm-message">{message}</p>
          {showDontShowAgain && (
            <label className="profile-checkbox confirm-dont-show">
              <input
                type="checkbox"
                checked={dontShow}
                onChange={(e) => {
                  setDontShow(e.target.checked);
                  try {
                    if (e.target.checked) localStorage.setItem('profiles.newProfileDialog.hide', '1');
                    else localStorage.removeItem('profiles.newProfileDialog.hide');
                  } catch { /* ignore */ }
                }}
              />
              <span className="profile-checkbox-box" aria-hidden="true">
                {dontShow && <i className="fas fa-check" />}
              </span>
              <span className="profile-checkbox-label">Don&apos;t show it again</span>
            </label>
          )}
        </div>
        <footer className="confirm-footer">
          <button type="button" className="profile-btn-secondary" onClick={onCancel} disabled={confirmDisabled}>{cancelLabel}</button>
          <button type="button" className="profile-btn-primary" onClick={onConfirm} disabled={confirmDisabled}>{confirmLabel}</button>
        </footer>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Worker safety settings tab                                                */
/* ------------------------------------------------------------------------ */

const SENSITIVITY_OPTIONS = ['Low', 'Medium', 'High'];

function WorkerSafetySettings({ form, setField }) {
  return (
    <>
      <h3 className="profile-section-title">FALL ALERT SETTINGS</h3>
      <div className="pp-card">
        <div className="pp-section-toggle">
          <ToggleSwitch
            checked={!!form.fallAlertEnabled}
            onChange={(v) => setField('fallAlertEnabled')({ target: { type: 'checkbox', checked: v } })}
            ariaLabel="Enable fall alert"
          />
        </div>
        <div className="profile-grid-2">
          <SelectField
            label="Sensitivity"
            value={form.fallAlertSensitivity}
            options={SENSITIVITY_OPTIONS}
            onChange={setField('fallAlertSensitivity')}
          />
          <TextField
            label="Stationary time before alarm (s)"
            value={form.stationaryTimeBeforeAlarmSeconds}
            onChange={setField('stationaryTimeBeforeAlarmSeconds')}
            type="number"
          />
          <TextField
            label="Time before alarm escalation (s)"
            value={form.timeBeforeAlarmEscalationSeconds}
            onChange={setField('timeBeforeAlarmEscalationSeconds')}
            type="number"
          />
        </div>
      </div>

      <h3 className="profile-section-title pp-section-title">LONE WORKER SETTING</h3>
      <div className="pp-card">
        <div className="pp-section-toggle">
          <ToggleSwitch
            checked={!!form.loneWorkerEnabled}
            onChange={(v) => setField('loneWorkerEnabled')({ target: { type: 'checkbox', checked: v } })}
            ariaLabel="Enable lone worker"
          />
        </div>
        <div className="profile-grid-2">
          <TextField
            label="Lone worker prompt timer (s)"
            value={form.loneWorkerPromptTimer}
            unit="s"
            onChange={setField('loneWorkerPromptTimer')}
            type="number"
          />
          <TextField
            label="Lone worker response timeout (s)"
            value={form.loneWorkerResponseTimeout}
            unit="s"
            onChange={setField('loneWorkerResponseTimeout')}
            type="number"
          />
        </div>
      </div>
    </>
  );
}

function ToggleSwitch({ checked, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      className={`rm-toggle ${checked ? 'on' : 'off'}`}
      onClick={() => onChange(!checked)}
    >
      <span className="rm-toggle-knob" />
    </button>
  );
}
